import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";
import { Loader2, Copy, Trash2, ShieldAlert, RefreshCw } from "lucide-react";

export default function DataToolsPanel() {
  const confirm = useConfirm();
  const [dups, setDups] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [visits, setVisits] = useState(null);
  const [visBusy, setVisBusy] = useState(false);

  const scanDups = async () => {
    setDupBusy(true);
    try { const { data } = await api.get("/admin/duplicates"); setDups(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDupBusy(false); }
  };

  const previewVisits = async () => {
    setVisBusy(true);
    try { const { data } = await api.get("/admin/test-visits"); setVisits(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVisBusy(false); }
  };

  const purge = async () => {
    if (!visits) return;
    const ok = await confirm({
      title: `Delete ${visits.delete_candidates} test visit${visits.delete_candidates !== 1 ? "s" : ""}?`,
      description: "This removes every visit that is NOT on a My-garages customer. Visits on your real customers are never touched. This cannot be undone.",
      confirmText: `Delete ${visits.delete_candidates}`, destructive: true,
    });
    if (!ok) return;
    setVisBusy(true);
    try {
      const { data } = await api.post("/admin/test-visits/purge");
      toast.success(`Deleted ${data.deleted} test visits`);
      window.dispatchEvent(new Event("jam:data"));
      previewVisits();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVisBusy(false); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="data-tools">
      <h2 className="text-lg font-black text-[#111827]">Data integrity</h2>

      {/* 1.1 duplicates */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Copy className="w-5 h-5 text-[#1E3A8A] mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-bold text-[#111827]">Find duplicate garages</div>
              <p className="text-xs text-[#6B7280]">Lists garages that appear more than once. Read-only — nothing is deleted.</p>
            </div>
          </div>
          <button onClick={scanDups} disabled={dupBusy} data-testid="scan-duplicates"
            className="h-11 px-4 rounded-lg bg-[#1E3A8A] text-white font-bold text-sm flex items-center gap-2 disabled:opacity-60 shrink-0">
            {dupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Scan
          </button>
        </div>
        {dups && (
          <div className="mt-3" data-testid="duplicates-result">
            {dups.total_groups === 0 ? (
              <p className="text-sm font-bold text-[#047857]">No duplicates found. 🎉</p>
            ) : (
              <>
                <p className="text-sm text-[#374151] mb-2"><b>{dups.total_groups}</b> duplicate group{dups.total_groups !== 1 ? "s" : ""} found.</p>
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {dups.groups.map((g, i) => (
                    <div key={i} className="border border-[#FCD34D] bg-[#FFFBEB] rounded-lg p-2.5" data-testid={`dup-group-${i}`}>
                      <div className="text-xs font-bold text-[#B45309] mb-1">{g.count}× · matched on {g.match_on === "pid" ? "Google place" : "name"}</div>
                      {g.garages.map((x) => (
                        <div key={x.id} className="text-sm text-[#111827] flex flex-wrap items-center gap-x-2">
                          <span className="font-bold">{x.name}</span>
                          <span className="text-xs text-[#6B7280]">{x.area} · {x.scope} · {x.visit_count} visits · {x.purchase_count} orders</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* 1.3 test visits cleanup */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <ShieldAlert className="w-5 h-5 text-[#B91C1C] mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-bold text-[#111827]">Clear test visits</div>
              <p className="text-xs text-[#6B7280]">Deletes visits that are NOT on a My-garages customer (accidental / test-account visits). Your customers' visits are never touched.</p>
            </div>
          </div>
          <button onClick={previewVisits} disabled={visBusy} data-testid="preview-test-visits"
            className="h-11 px-4 rounded-lg border border-[#D1D5DB] bg-white font-bold text-sm flex items-center gap-2 disabled:opacity-60 shrink-0">
            {visBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Preview
          </button>
        </div>
        {visits && (
          <div className="mt-3 space-y-2" data-testid="test-visits-result">
            <div className="text-sm text-[#374151]">
              Total visits: <b>{visits.total}</b> · On My garages (kept): <b className="text-[#047857]">{visits.keep_on_my_garages}</b> · To delete: <b className="text-[#B91C1C]">{visits.delete_candidates}</b>
            </div>
            {Object.keys(visits.by_user || {}).length > 0 && (
              <div className="text-xs text-[#6B7280]">By: {Object.entries(visits.by_user).map(([k, v]) => `${k} (${v})`).join(" · ")}</div>
            )}
            {visits.delete_candidates > 0 && (
              <button onClick={purge} disabled={visBusy} data-testid="purge-test-visits"
                className="h-11 px-4 rounded-lg bg-[#B91C1C] text-white font-bold text-sm flex items-center gap-2 disabled:opacity-60">
                <Trash2 className="w-4 h-4" /> Delete {visits.delete_candidates} test visit{visits.delete_candidates !== 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
